'use client'

import { Button, Stack, Text, Title } from '@mantine/core'
import { useRouter, useSearchParams } from 'next/navigation'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Sku } from '@/types/interfaces/sku'
import { ServiceItemCategory } from '@/types/enums/ServiceItem'
import DrawerOrModalContent from '@/components/shared/DrawerOrModalContent'
import { setInsuranceReturnPath } from '@/lib/redirectFromInsuranceFlow'
import { useGetInsurancePolicyQuery } from '@/lib/store/api/insurancePolicyApi'
import { getCookie } from 'cookies-next'
import { trackEvent, SegmentEvents, trackStoreEvent, StoreAnalytics, PageLocation } from '@nile/mantine-theme/analytics'
import { startLatencyTimer } from '@/lib/analytics'
import { type SkuCtaVariant, getSkuCtaStyles } from '@/components/SkuDetailCta.styles'

export type { SkuCtaVariant }

interface SkuDetailCtaProps {
    sku: Sku
    buttonSize?: 'lg' | 'md'
    /** When set, applies a theme from SkuDetailCta.styles (e.g. from Refresh category pages). Default is 'detail'. */
    variant?: SkuCtaVariant
    /** Override button label. */
    label?: string
    /** Optional; used when SkuDetailCta is rendered from a wrapper (e.g. SkuGetStartedCta) for analytics. */
    pageLocation?: PageLocation
    /** Optional; used when SkuDetailCta is rendered from a wrapper for analytics. */
    additionalInformation?: Record<string, unknown>
    /** When set, fires a SKU_PAGE_CTA_CLICKED event with the variant on click. */
    skuPageVariant?: 'refresh' | 'legacy'
    className?: string
    style?: React.CSSProperties
}

export function SkuDetailCta({
    sku,
    buttonSize = 'md',
    variant = 'detail',
    label,
    pageLocation = PageLocation.DETAIL_PAGE,
    additionalInformation = {},
    skuPageVariant,
    className,
    style,
}: SkuDetailCtaProps) {
    const [isLoading, setIsLoading] = useState(false)
    const [isPressed, setIsPressed] = useState(false)
    const [showInsuranceDrawer, setShowInsuranceDrawer] = useState(false)
    const router = useRouter()
    const searchParams = useSearchParams()
    const isAuthenticated = getCookie('isAuthenticated') === 'true'
    const { data: userInsurance, isLoading: userInsuranceLoading } = useGetInsurancePolicyQuery(undefined, {
        skip: !isAuthenticated,
    })
    const themeStyle = getSkuCtaStyles(variant)
    const resolvedTextColor = (style?.color as string | undefined) ?? themeStyle.c

    const buildUrl = (basePath: string) => {
        const ref = searchParams.get('ref')
        return ref ? `${basePath}?ref=${ref}` : basePath
    }

    const handleClick = async () => {
        trackStoreEvent(StoreAnalytics.CLICKED_SKU, {
            location: pageLocation,
            additionalInformation,
            sku_id: sku.id,
            sku_name: sku.name,
        })

        if (skuPageVariant) {
            trackEvent(SegmentEvents.SKU_PAGE_CTA_CLICKED, {
                sku_id: sku.id,
                button_type: 'get_started',
                variant: skuPageVariant,
            })
        }

        const isLabSku = sku.category === ServiceItemCategory.lab

        if (isLabSku && !userInsurance && sku.insurance_orderable === true) {
            setShowInsuranceDrawer(true)
            return
        }
        if (isLabSku) {
            setIsLoading(true)
            try {
                router.push(buildUrl(`/s/${sku.hyphenated_name}/${sku.id}/start`))
            } catch (error) {
                console.error('Error handling lab panel CTA click:', error)
                setIsLoading(false)
            }
            return
        }

        startLatencyTimer('get_started_to_next_screen')
        setIsLoading(true)
        try {
            router.push(buildUrl(`/s/${sku.hyphenated_name}/${sku.id}/screen`))
        } catch (error) {
            console.error('Error handling CTA click:', error)
            setIsLoading(false)
        }
    }

    const handleInsuranceChoice = async (useInsurance: boolean) => {
        setShowInsuranceDrawer(false)
        setIsLoading(true)
        try {
            if (useInsurance) {
                const refParam = searchParams.get('ref')
                setInsuranceReturnPath(buildUrl(`/s/${sku.hyphenated_name}/${sku.id}/start`), refParam)
                router.push(`/insurance`)
            } else {
                router.push(buildUrl(`/s/${sku.hyphenated_name}/${sku.id}/start`))
            }
        } catch (error) {
            console.error('Error handling insurance choice:', error)
            setIsLoading(false)
        }
    }

    const InsuranceDrawerContent = (
        <Stack gap="md" p="sm">
            <Stack gap={8} align="center">
                <Title order={3} c="var(--gm-gray-9)">
                    Do you have insurance you&apos;d like to use?
                </Title>
                <Text size="md" c="var(--gm-gray-6)">
                    You can change your mind later.
                </Text>
            </Stack>

            <Stack gap="md">
                <Button
                    onClick={() => handleInsuranceChoice(true)}
                    loading={isLoading || userInsuranceLoading}
                    size="lg"
                    radius="xl"
                    fw={500}
                    fz="md"
                    data-testid="yes-check-insurance-button"
                >
                    Yes, check my insurance
                </Button>

                <Button
                    onClick={() => handleInsuranceChoice(false)}
                    loading={isLoading || userInsuranceLoading}
                    variant="outline"
                    size="lg"
                    radius="xl"
                    fw={500}
                    fz="md"
                    c="var(--Foreground-fg-primary, #1F1E1D)"
                    data-testid="no-see-prices-button"
                >
                    No, see prices without insurance
                </Button>
            </Stack>
        </Stack>
    )

    const displayLabel = useMemo(() => {
        if (label) return label
        if (sku.category === ServiceItemCategory.lab) return 'Book your test now'
        return 'Get started'
    }, [label, sku.category])

    const isButtonLoading = isLoading || userInsuranceLoading
    const isDisabled = isButtonLoading || sku.state !== 'enabled'

    const isDetailVariant = variant === 'detail'

    return (
        <>
            <Button
                onClick={handleClick}
                loading={isButtonLoading}
                disabled={isDisabled}
                variant={
                    isDetailVariant ? 'filled' : themeStyle.variant === 'filled' ? 'scale-hover' : 'outline-scale-hover'
                }
                bg={isPressed ? 'blue.0' : themeStyle.bg}
                c={resolvedTextColor}
                w={
                    isDetailVariant
                        ? buttonSize === 'lg' && !isMobile
                            ? '50%'
                            : '100%'
                        : buttonSize === 'lg'
                          ? undefined
                          : 'fit-content'
                }
                px={isDetailVariant ? undefined : 24}
                py={isDetailVariant ? undefined : 16}
                radius={isDetailVariant ? '40px' : 999}
                size={isDetailVariant ? undefined : 'sm'}
                fz={isDetailVariant ? 16 : undefined}
                fw={isDetailVariant ? 'var(--weight-label, 550)' : 550}
                lh={isDetailVariant ? 'var(--gm-label-m-line-height)' : 1.4}
                h={isDetailVariant ? undefined : 'auto'}
                onMouseDown={() => setIsPressed(true)}
                onMouseUp={() => setIsPressed(false)}
                onMouseLeave={() => setIsPressed(false)}
                styles={{
                    root: {
                        ...(isDetailVariant ? { height: 48, maxWidth: 450 } : { flexShrink: 0, minHeight: 48 }),
                        ...themeStyle.rootStyles,
                    },
                }}
                className={className}
                style={style}
                data-testid={`sku-detail-cta-${sku?.hyphenated_name || 'button'}`}
            >
                {displayLabel}
            </Button>

            <DrawerOrModalContent
                content={InsuranceDrawerContent}
                showContent={showInsuranceDrawer}
                setShowContent={setShowInsuranceDrawer}
                onClose={() => setShowInsuranceDrawer(false)}
                withCloseButton={false}
            />
        </>
    )
}
